// Copyright 2026 Sorama B.V.
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";

/**
 * Mock implementation of IHardwareService<MeasurementDevice> that returns
 * fake CAM64 and CAM1K devices for local development.
 */
export class MeasurementDeviceServiceMock implements IHardwareService<MeasurementDevice> {
    public hardware: MeasurementDevice[] = [];
    public isRefreshing = false;

    private hardwarePromise: Promise<void> | null = null;

    public async refresh(): Promise<void> {
        if (this.hardwarePromise) {
            return this.hardwarePromise;
        }

        this.isRefreshing = true;
        this.hardwarePromise = this.retrieveHardware();
        try {
            await this.hardwarePromise;
        } finally {
            this.isRefreshing = false;
            this.hardwarePromise = null;
        }
    }

    private async retrieveHardware(): Promise<void> {
        await new Promise((resolve) => setTimeout(resolve, 500));

        this.hardware = [
            new MeasurementDevice(
                MeasurementDeviceServiceMock.makeFrontEnd("RMC201601014", "Sorama CAM64 201601014", 8, 8, 0.02),
                []
            ),
            new MeasurementDevice(
                MeasurementDeviceServiceMock.makeFrontEnd("RCA201601003", "Sorama CAM1K 201601003", 32, 32, 0.02),
                []
            ),
        ];
    }

    /** Generates a rectangular grid FrontEnd with realistic microphone positions. */
    private static makeFrontEnd(id: string, name: string, rows: number, cols: number, spacing: number) {
        const microphonePositions = [];
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                microphonePositions.push({
                    x: (c - (cols - 1) / 2) * spacing,
                    y: (r - (rows - 1) / 2) * spacing,
                    z: 0,
                });
            }
        }
        return {
            id,
            name,
            connectorCount: 1,
            microphoneCount: rows * cols,
            frontEndConnectors: [{ connectionInfo: "192.168.1.100", version: "0", microphoneCount: rows * cols }],
            microphonePositions,
        };
    }

    public getHardwareById(id: string): Promise<MeasurementDevice> {
        const device = this.hardware.find((d) => d.id === id);
        if (device) return Promise.resolve(device);
        return Promise.reject(`MeasurementDeviceServiceMock: device not found: ${id}`);
    }
}
